import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../components/feature/ProtectedRoute';
import AdminLayout from '../components/admin/AdminLayout';

// Lazy load pages
const HomePage = lazy(() => import('../pages/home/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));

// Auth pages
const LoginPage = lazy(() => import('../pages/auth/login/page'));
const SignupPage = lazy(() => import('../pages/auth/signup/page'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/forgot-password/page'));

// Driver pages
const DriverOnboardingPage = lazy(() => import('../pages/driver-onboarding/page'));
const DriverDashboardPage = lazy(() => import('../pages/driver/dashboard/page'));

// Client pages
const ClientRequestPage = lazy(() => import('../pages/client-request/page'));

// Admin pages
const AdminDashboardPage = lazy(() => import('../pages/admin/dashboard/page'));
const AdminRequestsPage = lazy(() => import('../pages/admin/requests/page'));
const AdminNotificationsPage = lazy(() => import('../pages/admin/notifications/page'));
const AccountManagementPage = lazy(() => import('../pages/admin/account-management/page'));
const AssignmentsPage = lazy(() => import('../pages/admin/assignments/page'));
const FinancialsPage = lazy(() => import('../pages/admin/financials/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/about',
    element: <AboutPage />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/auth/login',
    element: <LoginPage />,
  },
  {
    path: '/auth/signup',
    element: <SignupPage />,
  },
  {
    path: '/auth/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/driver-onboarding',
    element: <DriverOnboardingPage />,
  },
  {
    path: '/join-driver',
    element: <DriverOnboardingPage />,
  },
  {
    path: '/client-request',
    element: <ClientRequestPage />,
  },
  {
    path: '/request-driver',
    element: <ClientRequestPage />,
  },
  {
    path: '/driver/dashboard',
    element: (
      <ProtectedRoute allowedRoles={['driver']}>
        <DriverDashboardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/client/dashboard',
    element: <Navigate to="/client-request" replace />,
  },
  {
    path: '/admin/dashboard',
    element: (
      <ProtectedRoute allowedRoles={['ceo', 'co_ceo', 'staff']}>
        <AdminLayout title="Dashboard">
          <AdminDashboardPage />
        </AdminLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/requests',
    element: (
      <ProtectedRoute allowedRoles={['ceo', 'co_ceo', 'staff']}>
        <AdminLayout title="Requests">
          <AdminRequestsPage />
        </AdminLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/notifications',
    element: (
      <ProtectedRoute allowedRoles={['ceo', 'co_ceo', 'staff']}>
        <AdminLayout title="Notifications">
          <AdminNotificationsPage />
        </AdminLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/account-management',
    element: (
      <ProtectedRoute allowedRoles={['ceo', 'co_ceo']}>
        <AdminLayout title="Account Management">
          <AccountManagementPage />
        </AdminLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/assignments',
    element: (
      <ProtectedRoute allowedRoles={['ceo', 'co_ceo', 'staff']}>
        <AdminLayout title="Assignments">
          <AssignmentsPage />
        </AdminLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/financials',
    element: (
      <ProtectedRoute allowedRoles={['ceo', 'co_ceo', 'staff']}>
        <AdminLayout title="Financials">
          <FinancialsPage />
        </AdminLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard/financials',
    element: (
      <ProtectedRoute allowedRoles={['ceo', 'co_ceo', 'staff']}>
        <AdminLayout title="Financials">
          <FinancialsPage />
        </AdminLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default routes;
